import React, { useEffect } from 'react';

import { ModalStyled, ButtonStyled, ModalActionsStyled } from '@nike/nike-design-system-components';
import { useIdleTimer } from 'react-idle-timer';
import { useOktaAuth } from '@okta/okta-react';

import { setGlobalHeader } from '../../util/http';
import { ONE_SECOND, ONE_MINUTE } from '../../util/constants';

import { TranslatedCopy } from '../../intl/TranslatedCopy';

import useInterval from '../../hooks/useInterval';
import useModal from '../../hooks/useModal';

import logout from '../../util/logout';

/**
 * @name getTimeoutTime
 * @desc Calculates the time when app will logout due to inactivity
 * @param {number} remainingTime Number of milliseconds left until logout
 * @param {object} locale user's locale, needed to create a localized time
 */
const getTimeoutTime = (remainingTime, locale) => {
  const numberOfMlSeconds = Date.now();
  return new Date(numberOfMlSeconds + remainingTime)
    .toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) // display time in user's locale
    .replace(/^0+/, ''); // remove leading 0s in front of time
};

const Timer = () => {
  const [isOpen, openModal, closeModal] = useModal();

  const { authState, oktaAuth } = useOktaAuth();

  const accessTokenExpiresAt = authState?.accessToken?.expiresAt;

  useEffect(() => {
    const renewAccessToken = accessToken => {
      setGlobalHeader('Authorization', `Bearer ${accessToken}`);
    };

    // refresh access token when it expires after 1 hour
    oktaAuth.tokenManager.on('renewed', (key, newToken) => {
      if (key === 'accessToken' && newToken.accessToken) {
        renewAccessToken(newToken.accessToken);
      }
    });
  }, []);

  useInterval(() => {
    const currentTime = Math.floor(Date.now() / ONE_SECOND); // convert to seconds

    if (currentTime >= accessTokenExpiresAt) {
      logout(oktaAuth);
    }
  }, ONE_SECOND * 3);

  const onIdle = async () => {
    const isAuthenticated = await oktaAuth.isAuthenticated();

    if (isAuthenticated) {
      logout(oktaAuth);
    }
  };

  const onPrompt = async () => {
    const isAuthenticated = await oktaAuth.isAuthenticated();

    if (isAuthenticated) {
      openModal();
    }
  };

  const { reset, getRemainingTime } = useIdleTimer({
    timeout: ONE_MINUTE * 50,
    promptTimeout: ONE_MINUTE * 10,
    onIdle,
    onPrompt,
    startOnMount: false
  });

  const continueSession = () => {
    oktaAuth.tokenManager.renew('accessToken');
    oktaAuth.tokenManager.renew('idToken');
    closeModal();
    reset();
  };

  const actions = (
    <ModalActionsStyled>
      <ButtonStyled onClick={continueSession}>
        {new TranslatedCopy({ id: 'global.okta.continueSession' }).toString()}
      </ButtonStyled>
    </ModalActionsStyled>
  );

  return (
    <ModalStyled
      isOpen={isOpen}
      closeModal={continueSession}
      title={`${new TranslatedCopy({ id: 'global.okta.continueSession' }).toString()}?`}
      slots={{ FooterSlot: actions }}
      backdropCompProps={{ style: { backdropFilter: 'blur(4px)' } }}
      shouldCloseOnBackgroundClick={false}
    >
      {new TranslatedCopy({
        id: 'global.okta.timeoutMessage',
        values: { time: getTimeoutTime(getRemainingTime(), 'en-US') }
      }).toString()}
    </ModalStyled>
  );
};

export default Timer;
